import React, { useEffect, useRef } from 'react';

function isIndependenceDaySeason() {
  const now = new Date();
  return now.getMonth() === 7 && now.getDate() >= 4 && now.getDate() <= 16; // Aug 4–16
}

const CONTAINED_FLIGHT_DURATION_MS = 4200;
const CONTAINED_FLIGHT_PAUSE_MS = 2600;
const CONTAINED_TRAIL_LENGTH = 46;

const FULLSCREEN_FLIGHT_DURATION_MS = 8500;
const FULLSCREEN_FLIGHT_PAUSE_MS = 5000;
const FULLSCREEN_TRAIL_LENGTH = 90;

// One jet per formation slot, each carrying one tricolor band.
const FORMATION = [
  { color: '#FF9933', offsetX: -20, offsetY: -15 }, // left wing
  { color: '#F4F6FA', offsetX: 0, offsetY: 0 }, // lead
  { color: '#138808', offsetX: -20, offsetY: 15 }, // right wing
];

// Sleek top-down jet silhouette, nose pointing along +x, centered at origin.
function drawPlane(ctx, scale, fill) {
  ctx.beginPath();
  ctx.moveTo(11 * scale, 0);
  ctx.lineTo(2.7 * scale, 1 * scale);
  ctx.lineTo(0, 7.5 * scale);
  ctx.lineTo(1.3 * scale, 2 * scale);
  ctx.lineTo(-6.8 * scale, 1.3 * scale);
  ctx.lineTo(-8.9 * scale, 4 * scale);
  ctx.lineTo(-11 * scale, 0.8 * scale);
  ctx.lineTo(-8.9 * scale, -4 * scale);
  ctx.lineTo(-6.8 * scale, -1.3 * scale);
  ctx.lineTo(1.3 * scale, -2 * scale);
  ctx.lineTo(0, -7.5 * scale);
  ctx.lineTo(2.7 * scale, -1 * scale);
  ctx.closePath();

  ctx.shadowColor = 'rgba(13,31,60,0.3)';
  ctx.shadowBlur = 4;
  ctx.fillStyle = fill;
  ctx.fill();
  ctx.shadowBlur = 0;
  ctx.lineWidth = 0.6;
  ctx.strokeStyle = 'rgba(13,31,60,0.28)';
  ctx.stroke();
}

const IndependenceDayPlaneFormation = ({ fullScreen = false }) => {
  const canvasRef = useRef(null);
  const containerRef = useRef(null);

  const flightDuration = fullScreen ? FULLSCREEN_FLIGHT_DURATION_MS : CONTAINED_FLIGHT_DURATION_MS;
  const flightPause = fullScreen ? FULLSCREEN_FLIGHT_PAUSE_MS : CONTAINED_FLIGHT_PAUSE_MS;
  const trailLength = fullScreen ? FULLSCREEN_TRAIL_LENGTH : CONTAINED_TRAIL_LENGTH;

  useEffect(() => {
    if (!isIndependenceDaySeason()) return undefined;

    const canvas = canvasRef.current;
    const container = containerRef.current;
    const ctx = canvas.getContext('2d');
    let trails = FORMATION.map(() => []);
    let animationFrameId;
    let flightStartedAt = performance.now();

    const resizeCanvas = () => {
      canvas.width = fullScreen ? window.innerWidth : container.offsetWidth;
      canvas.height = fullScreen ? window.innerHeight : container.offsetHeight;
    };
    window.addEventListener('resize', resizeCanvas);
    resizeCanvas();

    const leadPath = (t) => {
      const startX = -40;
      const endX = canvas.width + 40;
      const startY = fullScreen ? canvas.height * 0.78 : canvas.height * 0.55;
      const endY = fullScreen ? canvas.height * 0.18 : canvas.height * 0.55;
      const y =
        startY + (endY - startY) * t + Math.sin(t * Math.PI * (fullScreen ? 2.4 : 1.4)) * (canvas.height * 0.12);
      return { x: startX + (endX - startX) * t, y };
    };

    const draw = () => {
      ctx.clearRect(0, 0, canvas.width, canvas.height);

      const elapsed = performance.now() - flightStartedAt;
      const flying = elapsed < flightDuration;

      if (flying) {
        const t = elapsed / flightDuration;
        const lead = leadPath(t);
        const leadTail = leadPath(Math.max(0, t - 0.02));
        const angle = Math.atan2(lead.y - leadTail.y, lead.x - leadTail.x);
        const cos = Math.cos(angle);
        const sin = Math.sin(angle);
        const spread = fullScreen ? 2 : 1;
        const planeScale = fullScreen ? 1.8 : 1;

        FORMATION.forEach((jet, i) => {
          const offsetX = jet.offsetX * spread;
          const offsetY = jet.offsetY * spread;
          const rotOffX = offsetX * cos - offsetY * sin;
          const rotOffY = offsetX * sin + offsetY * cos;
          const x = lead.x + rotOffX;
          const y = lead.y + rotOffY;
          const tailX = leadTail.x + rotOffX;
          const tailY = leadTail.y + rotOffY;

          trails[i].push({ x: tailX, y: tailY });
          if (trails[i].length > trailLength) trails[i].shift();

          for (let k = 1; k < trails[i].length; k++) {
            const a = trails[i][k - 1];
            const b = trails[i][k];
            const age = k / trails[i].length;
            ctx.beginPath();
            ctx.moveTo(a.x, a.y);
            ctx.lineTo(b.x, b.y);
            ctx.lineCap = 'round';
            ctx.lineWidth = (0.6 + age * 2.2) * planeScale;
            ctx.globalAlpha = age * 0.65;
            ctx.strokeStyle = jet.color;
            ctx.stroke();
          }
          ctx.globalAlpha = 1;

          ctx.save();
          ctx.translate(x, y);
          ctx.rotate(angle);
          drawPlane(ctx, planeScale, '#F4F6FA');
          ctx.restore();
        });
      } else {
        trails = FORMATION.map(() => []);
        if (elapsed > flightDuration + flightPause) {
          flightStartedAt = performance.now();
        }
      }

      animationFrameId = requestAnimationFrame(draw);
    };

    draw();

    return () => {
      cancelAnimationFrame(animationFrameId);
      window.removeEventListener('resize', resizeCanvas);
    };
  }, [fullScreen, flightDuration, flightPause, trailLength]);

  if (!isIndependenceDaySeason()) return null;

  return (
    <div
      ref={containerRef}
      style={
        fullScreen
          ? {
              position: 'fixed',
              inset: 0,
              zIndex: 1,
              pointerEvents: 'none',
              background: 'transparent',
            }
          : {
              position: 'relative',
              width: '100%',
              height: 90,
              overflow: 'hidden',
              background: 'linear-gradient(180deg, #eaf2ff 0%, #ffffff 100%)',
            }
      }
    >
      <canvas ref={canvasRef} style={{ width: '100%', height: '100%', display: 'block' }} />
    </div>
  );
};

export default IndependenceDayPlaneFormation;
